// src/services/staticApi.js
class StaticApiService {
  constructor() {
    this.cache = {
      years: null,
      breeds: null,
      statesByYear: null,
      schedules: new Map()
    };
    this.baseUrl = process.env.NODE_ENV === 'development' ? '' : '';
    this.isOnline = navigator.onLine;
    this.onlineListeners = new Set();
    
    // Setup online/offline listeners
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyOnlineStatusChange(true);
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyOnlineStatusChange(false);
    });
  }

  /**
   * Register a callback for online/offline status changes
   * @param {Function} callback - Function to call when status changes
   * @returns {Function} - Cleanup function to remove listener
   */
  onOnlineStatusChange(callback) {
    this.onlineListeners.add(callback);
    return () => this.onlineListeners.delete(callback);
  }

  /**
   * Notify all listeners of online status change
   * @param {boolean} isOnline - Current online status
   */
  notifyOnlineStatusChange(isOnline) {
    this.onlineListeners.forEach(callback => callback(isOnline));
  }

  /**
   * Get current online status
   * @returns {boolean} - True if online
   */
  getOnlineStatus() {
    return this.isOnline;
  }

  /**
   * Fetch JSON with offline fallback support
   * @param {string} url - URL to fetch
   * @returns {Promise<Object>} - JSON response
   */
  async fetchJson(url) {
    try {
      const response = await fetch(`${this.baseUrl}/data/${url}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      
      // Try to get from cache if network failed
      if ('caches' in window) {
        try {
          const cache = await caches.open('points-tool-data-v1.0.0');
          const cachedResponse = await cache.match(`${this.baseUrl}/data/${url}`);
          
          if (cachedResponse) {
            console.log(`Serving ${url} from cache fallback`);
            return await cachedResponse.json();
          }
        } catch (cacheError) {
          console.error('Cache fallback failed:', cacheError);
        }
      }
      
      throw error;
    }
  }

  async getYears() {
    if (!this.cache.years) {
      this.cache.years = await this.fetchJson('years.json');
    }
    return this.cache.years;
  }

  async getBreeds() {
    if (!this.cache.breeds) {
      this.cache.breeds = await this.fetchJson('breeds.json');
    }
    return this.cache.breeds;
  }

  async getStatesByYear() {
    if (!this.cache.statesByYear) {
      this.cache.statesByYear = await this.fetchJson('states-by-year.json');
    }
    return this.cache.statesByYear;
  }

  async getStates(year = new Date().getFullYear()) {
    const statesByYear = await this.getStatesByYear();
    return statesByYear[year.toString()] || [];
  }

  async getSchedule(state, breed, year = new Date().getFullYear()) {
    if (!state || !breed) {
      throw new Error('State and breed are required');
    }

    const cacheKey = `${year}-${state}-${breed}`;
    
    if (this.cache.schedules.has(cacheKey)) {
      return this.cache.schedules.get(cacheKey);
    }

    try {
      // Load the entire state file for the year
      const stateFileName = state.replace(/\s+/g, '_');
      const stateSchedules = await this.fetchJson(`schedules/${year}/${stateFileName}.json`);
      
      // Extract the specific breed data
      const breedData = stateSchedules[breed];
      
      if (!breedData) {
        throw new Error(`Schedule not found for ${breed} in ${state} for ${year}`);
      }

      const result = {
        dog_points: breedData.dog_points,
        bitch_points: breedData.bitch_points,
        division_number: breedData.division_number
      };

      // Cache the result
      this.cache.schedules.set(cacheKey, result);
      
      return result;
    } catch (error) {
      console.error(`Error fetching schedule for ${state}, ${breed}, ${year}:`, error);
      throw new Error('Schedule not found');
    }
  }

  async getMetadata() {
    return await this.fetchJson('metadata.json');
  }

  /**
   * Utility method to preload a state's data and cache it
   * @param {string} state - State name
   * @param {number} year - Year
   * @returns {Promise<string[]>} - List of breeds cached
   */
  async preloadStateData(state, year = new Date().getFullYear()) {
    try {
      const stateFileName = state.replace(/\s+/g, '_');
      const url = `schedules/${year}/${stateFileName}.json`;
      const stateSchedules = await this.fetchJson(url);
      
      // Cache all breeds for this state in memory
      Object.keys(stateSchedules).forEach(breed => {
        const cacheKey = `${year}-${state}-${breed}`;
        this.cache.schedules.set(cacheKey, stateSchedules[breed]);
      });
      
      // Also cache in service worker cache for offline access
      if ('caches' in window) {
        try {
          const cache = await caches.open('points-tool-schedules-v1.0.0');
          const response = new Response(JSON.stringify(stateSchedules), {
            headers: { 'Content-Type': 'application/json' }
          });
          await cache.put(`${this.baseUrl}/data/${url}`, response);
          console.log(`Cached ${state} data for offline access`);
        } catch (cacheError) {
          console.error('Failed to cache state data:', cacheError);
        }
      }
      
      return Object.keys(stateSchedules);
    } catch (error) {
      console.error(`Error preloading data for ${state}:`, error);
      return [];
    }
  }

  // Clear cache (useful for testing or manual refresh)
  clearCache() {
    this.cache = {
      years: null,
      breeds: null,
      statesByYear: null,
      schedules: new Map()
    };
  }

  /**
   * Get cache statistics including service worker cache
   * @returns {Promise<Object>} - Cache statistics
   */
  async getCacheStats() {
    const stats = {
      memory: {
        yearsLoaded: !!this.cache.years,
        breedsLoaded: !!this.cache.breeds,
        statesByYearLoaded: !!this.cache.statesByYear,
        scheduleCacheSize: this.cache.schedules.size
      },
      serviceWorker: {
        available: 'caches' in window,
        caches: []
      },
      online: this.isOnline
    };

    // Get service worker cache info
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        stats.serviceWorker.caches = cacheNames;
      } catch (error) {
        console.error('Failed to get cache names:', error);
      }
    }

    return stats;
  }

  /**
   * Check if data is available offline
   * @param {string} type - Type of data (metadata, schedule)
   * @param {Object} params - Parameters (year, state, breed for schedules)
   * @returns {Promise<boolean>} - True if data is cached
   */
  async isAvailableOffline(type, params = {}) {
    if (!('caches' in window)) {
      return false;
    }

    try {
      let url;
      let cacheName;

      if (type === 'metadata') {
        cacheName = 'points-tool-data-v1.0.0';
        const metadataFiles = ['years.json', 'breeds.json', 'states-by-year.json', 'metadata.json'];
        
        const cache = await caches.open(cacheName);
        const results = await Promise.all(
          metadataFiles.map(file => cache.match(`${this.baseUrl}/data/${file}`))
        );
        
        return results.every(response => response !== undefined);
      } else if (type === 'schedule' && params.state && params.year) {
        cacheName = 'points-tool-schedules-v1.0.0';
        const stateFileName = params.state.replace(/\s+/g, '_');
        url = `${this.baseUrl}/data/schedules/${params.year}/${stateFileName}.json`;
        
        const cache = await caches.open(cacheName);
        const response = await cache.match(url);
        
        return response !== undefined;
      }

      return false;
    } catch (error) {
      console.error('Error checking offline availability:', error);
      return false;
    }
  }
}

// Export a singleton instance
const staticApiService = new StaticApiService();
export default staticApiService;